import { useCallback, useMemo, useState } from 'react';
import { Box, Field, Flex, Spinner, VStack } from '@chakra-ui/react';
import type { Deck } from '../../types/deck';
import { DeckSearchSuggestionList } from './DeckSearchSuggestionList';
import type { DeckSearchSuggestionRowProps } from './DeckSearchSuggestionRow';
import { deckMatchesGalleryTextSearch } from '../../utils/deckGallerySearch';
import { RiftboundInput } from '../ui/input';

const DEFAULT_MAX_HEIGHT = 'min(228px, 42vh)';
const SEARCH_MAX_HEIGHT = 'min(420px, 62vh)';

export interface DeckSelectorProps {
  decks: Deck[];
  value?: string;
  onChange: (deckId: string | undefined) => void;
  loading?: boolean;
  label?: string;
  helperText?: string;
  /** Show a top row to clear the selection (e.g. "All decks"). */
  allowClear?: boolean;
  clearLabel?: string;
  searchPlaceholder?: string;
  maxHeight?: string;
  /**
   * Stretch the deck list to fill remaining parent height instead of a capped
   * `maxHeight`. The list stays overflow-scrollable. Parent must be a flex
   * column with a bounded height (`minH={0}`).
   */
  fillContainer?: boolean;
  hideScrollbar?: boolean;
  getRowProps?: (deck: Deck, index: number) => Partial<DeckSearchSuggestionRowProps>;
}

export function DeckSelector({
  decks,
  value,
  onChange,
  loading = false,
  label,
  helperText,
  allowClear = false,
  clearLabel = 'All decks',
  searchPlaceholder = 'Filter decks…',
  maxHeight,
  fillContainer = false,
  hideScrollbar = false,
  getRowProps,
}: DeckSelectorProps) {
  const [search, setSearch] = useState('');
  const searching = Boolean(search.trim());

  const displayedDecks = useMemo(() => {
    if (searching) {
      return decks.filter((deck) => deckMatchesGalleryTextSearch(deck, search));
    }
    return decks;
  }, [decks, search, searching]);

  const resolvedMaxHeight = fillContainer
    ? undefined
    : (maxHeight ?? (searching ? SEARCH_MAX_HEIGHT : DEFAULT_MAX_HEIGHT));
  const fillProps = fillContainer
    ? { flex: '1' as const, minH: '0', display: 'flex', flexDirection: 'column' as const }
    : undefined;

  const baseRowProps = useCallback(
    (deck: Deck, index: number) => ({
      active: deck.id === value,
      onActivate: () => onChange(deck.id),
      ...getRowProps?.(deck, index),
    }),
    [getRowProps, onChange, value],
  );

  const content = loading ? (
    <Flex justify="center" py={4} {...fillProps}>
      <Spinner size="sm" color="accent" />
    </Flex>
  ) : (
    <VStack align="stretch" gap={2} w="100%" overflow="hidden" {...fillProps}>
      {decks.length > 1 ? (
        <RiftboundInput
          placeholder={searchPlaceholder}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          autoComplete="off"
          spellCheck={false}
          borderColor="border"
          flexShrink={0}
        />
      ) : null}

      {allowClear ? (
        <Box
          as="button"
          w="100%"
          flexShrink={0}
          textAlign="left"
          borderRadius="md"
          borderWidth="hairline"
          borderColor={!value ? 'accentMuted' : 'border'}
          bg={!value ? 'bg.emphasized' : 'bgElevated'}
          px={3}
          py={2}
          fontSize="sm"
          fontWeight="semibold"
          color={!value ? 'text' : 'textMuted'}
          cursor="pointer"
          _hover={{
            bg: !value ? undefined : 'bg.emphasized',
            borderColor: !value ? undefined : 'accentMuted',
            color: 'text',
          }}
          onClick={() => onChange(undefined)}
        >
          {clearLabel}
        </Box>
      ) : null}

      <DeckSearchSuggestionList
        decks={displayedDecks}
        emptyMessage={searching ? `No decks match "${search.trim()}"` : undefined}
        maxHeight={resolvedMaxHeight}
        fillContainer={fillContainer}
        hideScrollbar={hideScrollbar}
        getRowProps={baseRowProps}
      />
    </VStack>
  );

  if (!label && !helperText) {
    return content;
  }

  return (
    <Field.Root
      display={fillContainer ? 'flex' : undefined}
      flexDirection={fillContainer ? 'column' : undefined}
      {...fillProps}
    >
      {label ? <Field.Label flexShrink={0}>{label}</Field.Label> : null}
      {content}
      {helperText ? (
        <Field.HelperText flexShrink={0} color="textMuted">
          {helperText}
        </Field.HelperText>
      ) : null}
    </Field.Root>
  );
}
